import type { ClientMessage, ServerMessage } from '../../../shared/protocol.ts';
import { ServerClock } from './clock.ts';
import { Emitter, localNow } from './emitter.ts';

type Events = {
  message: ServerMessage;
  open: void;
  close: { code: number; willRetry: boolean };
};

/**
 * WebSocket link to the studio. Reconnects with backoff, keeps the server clock in sync,
 * and never blocks recording: local capture carries on even while this is down.
 */
export class Signaling extends Emitter<Events> {
  readonly clock = new ServerClock();
  connected = false;
  private ws: WebSocket | null = null;
  private retry = 0;
  private closed = false;
  private timeId = 0;
  private syncTimer: number | undefined;
  private url: string;
  private hello: () => ClientMessage;

  constructor(studioId: string, key: string, hello: () => ClientMessage) {
    super();
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    this.url = `${proto}://${location.host}/ws?studio=${encodeURIComponent(studioId)}&k=${encodeURIComponent(key)}`;
    this.hello = hello;
    this.connect();
  }

  private connect() {
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.connected = true;
      this.retry = 0;
      ws.send(JSON.stringify(this.hello()));
      this.burstSync();
      this.emit('open', undefined);
    };
    ws.onmessage = (ev) => {
      const msg = JSON.parse(ev.data) as ServerMessage;
      if (msg.type === 'time') {
        this.clock.addSample(msg.clientSent, msg.serverTime);
        return;
      }
      this.emit('message', msg);
    };
    ws.onclose = (ev) => {
      this.connected = false;
      clearInterval(this.syncTimer);
      const willRetry = !this.closed && ev.code !== 4401 && ev.code !== 4403 && ev.code !== 4404;
      this.emit('close', { code: ev.code, willRetry });
      if (willRetry) {
        const delay = Math.min(10_000, 500 * 2 ** this.retry++) * (0.75 + Math.random() / 2);
        setTimeout(() => this.connect(), delay);
      }
    };
  }

  /** A burst of quick pings for an accurate first estimate, then a slow trickle to track drift. */
  private burstSync() {
    let n = 0;
    const burst = setInterval(() => {
      this.ping();
      if (++n >= 10) clearInterval(burst);
    }, 120);
    this.syncTimer = window.setInterval(() => this.ping(), 5000);
  }

  private ping() {
    this.send({ type: 'time', id: ++this.timeId, clientSent: localNow() });
  }

  send(msg: ClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }

  close() {
    this.closed = true;
    clearInterval(this.syncTimer);
    this.ws?.close();
  }
}
